import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Image as ImageIcon, X, Sparkles, ArrowLeft, Loader2, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import RecipeForm from "@/components/RecipeForm";
import DocumentScanner from "@/components/DocumentScanner";
import DishCrop from "@/components/DishCrop";

export default function Scan() {
  const navigate = useNavigate();
  const cameraRef = useRef();
  const galleryRef = useRef();
  const [images, setImages] = useState([]); // {id, path, url}
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [mainId, setMainId] = useState(null);
  const [cropBox, setCropBox] = useState(null);
  const effectiveMain = mainId || (images[0] && images[0].id);

  useEffect(() => {
    if (!draft) { setCropBox(null); return; }
    const b = draft.dish_photo_bbox;
    setCropBox(b && b.width ? { x: b.x, y: b.y, width: b.width, height: b.height } : { x: 0.1, y: 0.1, width: 0.8, height: 0.8 });
  }, [draft]);

  const uploadBlob = async (blob) => {
    setScannerOpen(false);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], `scan_${Date.now()}.jpg`, { type: "image/jpeg" }));
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImages((prev) => [...prev, { id: res.data.id, path: res.data.path, url: fileUrl(res.data.path) }]);
      toast.success("Fiche scannée ajoutée");
    } catch {
      toast.error("Échec de l'ajout du scan");
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, { id: res.data.id, path: res.data.path, url: fileUrl(res.data.path) }]);
      }
    } catch {
      toast.error("Échec de l'envoi de la photo");
    } finally {
      setUploading(false);
    }
  };

  const analyze = async () => {
    if (!images.length) return;
    setAnalyzing(true);
    try {
      const res = await api.post("/scan", { image_ids: images.map((i) => i.id) });
      setDraft(res.data);
      toast.success("Recette analysée !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "L'analyse a échoué. Réessayez.");
    } finally {
      setAnalyzing(false);
    }
  };

  const save = async (payload) => {
    setSaving(true);
    try {
      // On recadre la photo du plat selon le cadre ajusté par l'utilisateur (sinon recadrage IA).
      const base = images[0];
      let dishPath = draft?.dish_crop_path || null;
      if (base && cropBox) {
        try {
          const { data } = await api.post("/files/crop", { path: base.path, bbox: cropBox });
          dishPath = data.path;
        } catch { /* garde le recadrage IA si dispo */ }
      }
      const dish = images.find((i) => i.id === effectiveMain) || images[0];
      payload.images = dishPath ? [dishPath] : dish ? [dish.path] : [];
      payload.source = "hellofresh";
      const res = await api.post("/recipes", payload);
      const others = images.map((i) => i.path).filter((p) => p && p !== payload.images[0]);
      if (others.length) api.post("/files/delete", { paths: others }).catch(() => {});
      toast.success("Recette enregistrée dans votre bibliothèque");
      navigate(`/recipes/${res.data.id}`);
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Étape validation
  if (draft) {
    return (
      <div className="px-5 pt-8" data-testid="scan-review-page">
        <button onClick={() => setDraft(null)} className="mb-3 flex items-center gap-1 text-sm font-medium at-press" style={{ color: "var(--at-text-2)" }}>
          <ArrowLeft size={16} /> Reprendre les photos
        </button>
        <div className="mb-5 rounded-2xl border p-4" style={{ background: "var(--at-sage-light)", borderColor: "transparent" }}>
          <h1 className="font-display text-2xl font-bold">Voici ce que j'ai compris</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--at-text-2)" }}>Corrigez les éléments marqués <span className="font-semibold" style={{ color: "#92400E" }}>À vérifier</span>, puis enregistrez.</p>
        </div>
        {images[0] && cropBox && (
          <div className="mb-5" data-testid="dish-crop-section">
            <h2 className="mb-1 font-display text-lg font-semibold">Photo du plat</h2>
            <p className="mb-2 text-xs" style={{ color: "var(--at-text-2)" }}>Ajustez le cadre pour ne garder que l'assiette (déplacez le cadre, tirez les coins).</p>
            <DishCrop url={images[0].url} box={cropBox} onChange={setCropBox} />
          </div>
        )}
        <RecipeForm initial={draft} onSave={save} saving={saving} submitLabel="Enregistrer la recette" />
      </div>
    );
  }

  return (
    <div className="px-5 pt-8" data-testid="scan-page">
      <header className="at-fade-up">
        <h1 className="font-display text-3xl font-bold">Scanner une recette</h1>
        <p className="mt-1 text-sm" style={{ color: "var(--at-text-2)" }}>
          Photographiez votre fiche HelloFresh (recto, verso, détails). Elle peut être un peu de travers, l'IA s'en charge.
        </p>
      </header>

      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="camera-input"
        onChange={(e) => handleFiles(e.target.files)} />
      <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" data-testid="gallery-input"
        onChange={(e) => handleFiles(e.target.files)} />

      <button data-testid="auto-scan-btn" onClick={() => setScannerOpen(true)}
        className="mt-6 flex w-full items-center gap-3 rounded-3xl border-2 p-5 text-left at-press at-fade-up"
        style={{ background: "var(--at-brand-light)", borderColor: "var(--at-brand)", color: "var(--at-brand)" }}>
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: "var(--at-brand)" }}>
          <ScanLine size={26} />
        </span>
        <span>
          <span className="block font-display text-lg font-bold">Scanner automatique</span>
          <span className="block text-sm" style={{ color: "var(--at-text-2)" }}>Détecte la fiche et capture toute seule, en redressant l'image</span>
        </span>
      </button>

      <div className="mt-3 grid grid-cols-2 gap-3 at-fade-up" style={{ animationDelay: "60ms" }}>
        <button data-testid="take-photo-btn" onClick={() => cameraRef.current?.click()}
          className="at-press flex flex-col items-center gap-2 rounded-3xl border-2 border-black/5 bg-white p-6 at-shadow"
          style={{ color: "var(--at-brand)" }}>
          <Camera size={32} /> <span className="font-semibold">Prendre une photo</span>
        </button>
        <button data-testid="pick-photo-btn" onClick={() => galleryRef.current?.click()}
          className="at-press flex flex-col items-center gap-2 rounded-3xl border-2 border-black/5 bg-white p-6 at-shadow"
          style={{ color: "var(--at-sage)" }}>
          <ImageIcon size={32} /> <span className="font-semibold">Depuis la galerie</span>
        </button>
      </div>

      {uploading && (
        <p className="mt-4 flex items-center gap-2 text-sm" style={{ color: "var(--at-text-2)" }}>
          <Loader2 size={16} className="animate-spin" /> Envoi de la photo...
        </p>
      )}

      {images.length > 0 && (
        <section className="mt-6 at-fade-up">
          <h2 className="mb-1 font-display text-lg font-semibold">{images.length} photo{images.length > 1 ? "s" : ""}</h2>
          <p className="mb-3 text-xs" style={{ color: "var(--at-muted)" }}>Touchez l'étoile sur la photo du plat : elle sera l'image de la recette.</p>
          <div className="grid grid-cols-3 gap-3">
            {images.map((img, i) => (
              <div key={img.id} className="relative aspect-square overflow-hidden rounded-2xl at-shadow at-pop" style={{ background: "var(--at-elevated)" }} data-testid={`scan-thumb-${i}`}>
                <img src={img.url} alt="" className="h-full w-full object-contain" />
                <button onClick={() => setMainId(img.id)} data-testid={`set-main-${i}`}
                  className="absolute left-1 top-1 flex h-7 items-center gap-1 rounded-full px-2 text-[10px] font-bold backdrop-blur"
                  style={img.id === effectiveMain ? { background: "var(--at-brand)", color: "#fff" } : { background: "rgba(255,255,255,0.8)", color: "var(--at-text-2)" }}>
                  ★ {img.id === effectiveMain ? "Plat" : ""}
                </button>
                <button onClick={() => { setImages((p) => p.filter((x) => x.id !== img.id)); if (mainId === img.id) setMainId(null); }} data-testid={`remove-thumb-${i}`}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white">
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {images.length > 0 && (
        <Button data-testid="analyze-btn" onClick={analyze} disabled={analyzing}
          className="mt-6 h-14 w-full rounded-2xl text-base font-semibold text-white at-shadow-lg at-press"
          style={{ background: "var(--at-brand)" }}>
          {analyzing ? <><Loader2 size={20} className="mr-2 animate-spin" /> Analyse en cours...</> : <><Sparkles size={20} className="mr-2" /> Analyser la recette</>}
        </Button>
      )}

      <div className="mt-6 text-center">
        <button onClick={() => navigate("/add")} data-testid="manual-add-link" className="text-sm font-medium underline" style={{ color: "var(--at-text-2)" }}>
          Ou ajouter une recette manuellement
        </button>
      </div>

      {scannerOpen && <DocumentScanner onCapture={uploadBlob} onClose={() => setScannerOpen(false)} />}
    </div>
  );
}
